import { FC } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { isNil } from 'lodash';
import numeral from 'numeral';
import { DailyNavBreakdownCompRow } from 'api/models/DailyNavBreakdownCompRow';
import { DailyNavBreakdownWindowSummary } from 'api/models/DailyNavBreakdownWindowSummary';
import { shortDate } from 'utilities';
import { DeltaBreakdownDialogProps } from './DeltaBreakdownDialog.props';

// Mirrors the DeltaCell/ReviewPriceDialog signed-percent format exactly.
const formatSignedPercent = (value: string | null | undefined) => {
  if (isNil(value)) {
    return '—';
  }
  const numericValue = Number(value);
  return `${numericValue > 0 ? '+' : ''}${(numericValue * 100).toFixed(1)}%`;
};

// Market caps arrive in millions; render as compact currency (e.g. "$345.8b").
const formatMarketCap = (value: string | null | undefined) =>
  isNil(value) ? '—' : numeral(Number(value) * 1e6).format('$0.0a');

const formatThreshold = (summary: DailyNavBreakdownWindowSummary) => {
  if (isNil(summary.threshold) || summary.threshold_source === 'none') {
    return '—';
  }
  return `${(Number(summary.threshold) * 100).toFixed(1)}% (${summary.threshold_source})`;
};

// Reasons a comp is excluded from the average, humanized for display.
const EXCLUDED_REASON_LABELS: Record<string, string> = {
  no_ticker: 'no ticker',
  unresolved: 'unresolved in CapIQ',
  no_data: 'no market data',
};

const humanizeExcludedReason = (reason: string) =>
  EXCLUDED_REASON_LABELS[reason] ?? reason.replace(/_/g, ' ');

const compLabel = (comp: DailyNavBreakdownCompRow) =>
  comp.ticker ? `${comp.company_name} (${comp.ticker})` : comp.company_name;

const DIVIDER_ROW_SX = { borderTop: 2, borderColor: 'divider' };

// A per-comp window cell: the signed percent, with a hover tooltip revealing the
// two market caps and the dates they were pulled for (so the math is inspectable).
interface WindowPctCellProps {
  pct: string | null;
  excluded: boolean;
  currentMarketCap: string | null;
  currentDate?: string;
  priorMarketCap: string | null;
  priorDate?: string | null;
}

const WindowPctCell: FC<WindowPctCellProps> = ({
  pct,
  excluded,
  currentMarketCap,
  currentDate,
  priorMarketCap,
  priorDate,
}) => {
  if (excluded || isNil(pct)) {
    return (
      <TableCell align="right" sx={excluded ? { color: 'text.disabled' } : undefined}>
        —
      </TableCell>
    );
  }
  return (
    <Tooltip
      arrow
      title={
        <Box sx={{ whiteSpace: 'nowrap' }}>
          <div>{`${shortDate(priorDate ?? undefined) ?? '—'}: ${formatMarketCap(priorMarketCap)}`}</div>
          <div>{`${shortDate(currentDate) ?? '—'}: ${formatMarketCap(currentMarketCap)}`}</div>
        </Box>
      }>
      <TableCell align="right" sx={{ cursor: 'help' }}>
        {formatSignedPercent(pct)}
      </TableCell>
    </Tooltip>
  );
};

const DeltaBreakdownDialog: FC<DeltaBreakdownDialogProps> = ({ row, open, onClose }) => {
  // The breakdown is delivered inline on each record by the list endpoint, so
  // there is nothing to fetch — the dialog opens with no extra request.
  const data = row?.record.breakdown;
  const dates = data?.dates;

  return (
    <Dialog id="delta-breakdown-dialog" data-testid="delta-breakdown-dialog" open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{`${row?.companyName ?? ''} — Delta Breakdown`}</DialogTitle>
      <DialogContent>
        {dates && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', marginBottom: 1.5 }}>
            {`Current: ${shortDate(dates.current) ?? '—'} · T-1: ${shortDate(dates.prior_one_day) ?? '—'} · T-5: ${
              shortDate(dates.prior_five_day) ?? '—'
            } · Last Valuation: ${dates.last_valuation ? shortDate(dates.last_valuation) ?? '—' : '—'}`}
          </Typography>
        )}

        {!data && (
          <Typography variant="body2" sx={{ color: 'text.secondary', paddingY: 2 }}>
            No breakdown data available.
          </Typography>
        )}

        {data && (
          <Table id="delta-breakdown-table" data-testid="delta-breakdown-table" size="small">
            <TableHead>
              <TableRow>
                <TableCell>Comp</TableCell>
                <TableCell align="right">Market Cap</TableCell>
                <TableCell align="right">1-Day</TableCell>
                <TableCell align="right">5-Day</TableCell>
                <TableCell align="right">Since Last Val</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.comps.map(comp => {
                const excluded = !isNil(comp.excluded_reason);
                const rowSx = excluded ? { color: 'text.disabled' } : {};
                return (
                  <TableRow key={`${comp.company_name}-${comp.ticker ?? comp.comp_type}`}>
                    <TableCell sx={rowSx}>
                      {compLabel(comp)}
                      {excluded && (
                        <Typography component="span" variant="caption" sx={{ color: 'text.disabled', marginLeft: 0.5 }}>
                          {`(${humanizeExcludedReason(comp.excluded_reason as string)})`}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={rowSx}>
                      {formatMarketCap(comp.current_market_cap)}
                    </TableCell>
                    <WindowPctCell
                      excluded={excluded}
                      pct={comp.one_day.pct}
                      currentMarketCap={comp.current_market_cap}
                      currentDate={data.dates.current}
                      priorMarketCap={comp.one_day.prior_market_cap}
                      priorDate={data.dates.prior_one_day}
                    />
                    <WindowPctCell
                      excluded={excluded}
                      pct={comp.five_day.pct}
                      currentMarketCap={comp.current_market_cap}
                      currentDate={data.dates.current}
                      priorMarketCap={comp.five_day.prior_market_cap}
                      priorDate={data.dates.prior_five_day}
                    />
                    <WindowPctCell
                      excluded={excluded}
                      pct={comp.since_last_valuation.pct}
                      currentMarketCap={comp.current_market_cap}
                      currentDate={data.dates.current}
                      priorMarketCap={comp.since_last_valuation.prior_market_cap}
                      priorDate={data.dates.last_valuation}
                    />
                  </TableRow>
                );
              })}

              <TableRow>
                <TableCell sx={{ ...DIVIDER_ROW_SX, fontWeight: 600 }}>Average</TableCell>
                <TableCell sx={DIVIDER_ROW_SX} />
                <TableCell align="right" sx={{ ...DIVIDER_ROW_SX, fontWeight: 600 }}>
                  {formatSignedPercent(data.one_day.average)}
                </TableCell>
                <TableCell align="right" sx={{ ...DIVIDER_ROW_SX, fontWeight: 600 }}>
                  {formatSignedPercent(data.five_day.average)}
                </TableCell>
                <TableCell align="right" sx={{ ...DIVIDER_ROW_SX, fontWeight: 600 }}>
                  {formatSignedPercent(data.since_last_valuation.average)}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell>Threshold</TableCell>
                <TableCell />
                <TableCell align="right">{formatThreshold(data.one_day)}</TableCell>
                <TableCell align="right">{formatThreshold(data.five_day)}</TableCell>
                <TableCell align="right">{formatThreshold(data.since_last_valuation)}</TableCell>
              </TableRow>

              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell />
                {[data.one_day, data.five_day, data.since_last_valuation].map((summary, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <TableCell key={index} align="right" sx={{ color: summary.exceeded ? 'error.main' : 'text.secondary', fontWeight: summary.exceeded ? 600 : 400 }}>
                    {summary.exceeded ? 'BREACH' : 'OK'}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DeltaBreakdownDialog;
